import type { TournamentSummary, TournamentPreview } from '../shared/tournament-types.js';
import type { MenuAction } from './MenuView.js';
import { formatCountdown } from './Countdown.js';
import type { PlayerData } from '../collection/PlayerState.js';
import {
  renderTournamentDeckPicker,
  setTournamentDeckId,
} from '../collection/TournamentDeckPrefs.js';

/** List view: one card per tournament summary, ordered by:
 *    1. Active tournaments the player is entered in
 *    2. Active tournaments the player hasn't joined
 *    3. Unclaimed prizes from ended tournaments
 *
 *  Returns a teardown function for any timers the view registered so
 *  the router can stop them when the view unmounts. (The countdowns
 *  re-render every second to stay current.) */
export function createTournamentListView(
  container: HTMLElement,
  tournaments: TournamentSummary[],
  previews: TournamentPreview[],
  playerData: PlayerData,
  onAction: (action: MenuAction) => void,
): () => void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen tournament-list is-scrollable';

  const sorted = [...tournaments].sort((a, b) => {
    const aPriority = priorityFor(a);
    const bPriority = priorityFor(b);
    if (aPriority !== bPriority) return aPriority - bPriority;
    return new Date(a.endAt).getTime() - new Date(b.endAt).getTime();
  });

  el.innerHTML = `
    <button class="menu-btn campaign-back" id="back-btn">&larr; Back</button>
    <h1 class="menu-title">Tournaments</h1>
    <div class="tournament-cards"></div>
  `;

  const list = el.querySelector('.tournament-cards')!;
  if (sorted.length === 0 && previews.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'tournament-empty';
    empty.textContent = 'No tournaments are running right now. Check back soon.';
    list.appendChild(empty);
  }

  for (const t of sorted) {
    list.appendChild(buildCard(t, playerData, onAction));
  }
  // Preview cards go at the end — they can't be joined, so they
  // shouldn't compete for the player's eye with actionable cards.
  // Sort soonest-first so a tournament starting in 2d appears above
  // one starting in 9d.
  const sortedPreviews = [...previews].sort(
    (a, b) => new Date(a.nextStartAt).getTime() - new Date(b.nextStartAt).getTime(),
  );
  for (const p of sortedPreviews) {
    list.appendChild(buildPreviewCard(p));
  }

  el.querySelector('#back-btn')!.addEventListener('click', () => {
    onAction({ type: 'back-to-menu' });
  });

  container.appendChild(el);

  // Tick countdowns once per second. We don't recompute everything —
  // just rewrite the `.tournament-card-countdown` text of each card.
  // Keeps the view responsive without per-second DOM thrash. Preview
  // cards use `data-starts-at` and prefix the text with "Starts in ".
  const tick = setInterval(() => {
    for (const card of list.querySelectorAll<HTMLElement>('.tournament-card')) {
      const span = card.querySelector('.tournament-card-countdown');
      if (!span) continue;
      const startsAt = card.getAttribute('data-starts-at');
      if (startsAt) {
        span.textContent = `Starts in ${formatCountdown(new Date(startsAt).getTime())}`;
        continue;
      }
      const id = card.getAttribute('data-tournament-id');
      if (!id) continue;
      const t = sorted.find(s => s.id === id);
      if (!t) continue;
      span.textContent = t.isEnded
        ? 'Ended'
        : formatCountdown(new Date(t.endAt).getTime());
    }
  }, 1000);

  return () => clearInterval(tick);
}

function priorityFor(t: TournamentSummary): number {
  if (t.unclaimedPrize) return 0;        // claim prizes first (rare action)
  if (t.entered && t.isActive) return 1; // ongoing — show prominently
  if (t.isActive) return 2;              // joinable
  return 3;                              // ended without prize
}

function buildCard(t: TournamentSummary, playerData: PlayerData, onAction: (a: MenuAction) => void): HTMLElement {
  const card = document.createElement('div');
  card.className = 'tournament-card';
  card.setAttribute('data-tournament-id', t.id);

  const countdownText = t.isEnded
    ? 'Ended'
    : formatCountdown(new Date(t.endAt).getTime());

  const stateBadge = t.isEnded
    ? '<span class="tournament-state-badge ended">Ended</span>'
    : '<span class="tournament-state-badge active">Active</span>';
  // One-off or pattern-cycling tournaments get a "Special!" chip so
  // the player notices they're limited-run compared to the evergreen
  // weekly ladder.
  const specialBadge = t.isSpecial
    ? '<span class="tournament-state-badge special">Special!</span>'
    : '';

  const rankInner = t.entered && t.playerRank != null
    ? `Your rank: <strong>#${t.playerRank}</strong> of ${t.totalEntries}`
    : `${t.totalEntries} players`;
  // Rank + deck picker share one row to keep the card compact. Deck
  // picker only renders when the tournament's still active —
  // ended/claimable cards have no match to launch. When omitted, the
  // rank line stretches to fill the row alone.
  const deckPickerHtml = t.isActive
    ? renderTournamentDeckPicker(t.defId, playerData)
    : '';
  const rankLine = `
    <div class="tournament-card-rank-row">
      <span class="tournament-card-rank">${rankInner}</span>
      ${deckPickerHtml}
    </div>
  `;

  let ctaHtml = '';
  if (t.unclaimedPrize) {
    ctaHtml = `<button class="menu-btn primary-purple" data-action="claim">Claim Prize</button>`;
  } else if (t.isActive) {
    const label = t.entered ? 'Play Match' : 'Join &amp; Play';
    ctaHtml = `
      <button class="menu-btn primary-purple" data-action="play">${label}</button>
      <button class="menu-btn-sm" data-action="details">Details</button>
    `;
  } else {
    ctaHtml = `<button class="menu-btn-sm" data-action="details">Details</button>`;
  }

  card.innerHTML = `
    <div class="tournament-card-header">
      <h2 class="tournament-card-title">${escapeHtml(t.displayName)}</h2>
      ${specialBadge}
      ${stateBadge}
    </div>
    <p class="tournament-card-desc">${escapeHtml(t.description)}</p>
    ${rankLine}
    <div class="tournament-card-footer">
      <span class="tournament-card-countdown">${countdownText}</span>
      <div class="tournament-card-actions">${ctaHtml}</div>
    </div>
  `;

  card.querySelector('[data-action="play"]')?.addEventListener('click', () => {
    onAction({ type: 'play-tournament', tournamentId: t.id });
  });
  card.querySelector('[data-action="claim"]')?.addEventListener('click', () => {
    onAction({ type: 'claim-tournament-prize', tournamentId: t.id });
  });
  card.querySelector('[data-action="details"]')?.addEventListener('click', () => {
    onAction({ type: 'view-tournament', tournamentId: t.id });
  });
  const deckSelect = card.querySelector<HTMLSelectElement>('.tournament-deck-picker select');
  deckSelect?.addEventListener('change', () => {
    setTournamentDeckId(t.defId, deckSelect.value);
  });
  return card;
}


/** "Coming Soon" preview card for a pattern-based tournament whose
 *  current occurrence is off. Same visual chassis as an active card
 *  so the layout doesn't reflow, but without CTAs — nothing to join
 *  yet — and with a countdown-to-start instead of countdown-to-end. */
function buildPreviewCard(p: TournamentPreview): HTMLElement {
  const card = document.createElement('div');
  card.className = 'tournament-card tournament-card-preview';
  card.setAttribute('data-def-id', p.defId);
  card.setAttribute('data-starts-at', p.nextStartAt);
  const initialCountdown = `Starts in ${formatCountdown(new Date(p.nextStartAt).getTime())}`;
  card.innerHTML = `
    <div class="tournament-card-header">
      <h2 class="tournament-card-title">${escapeHtml(p.displayName)}</h2>
      <span class="tournament-state-badge special">Special!</span>
      <span class="tournament-state-badge coming-soon">Coming Soon</span>
    </div>
    <p class="tournament-card-desc">${escapeHtml(p.description)}</p>
    <div class="tournament-card-footer">
      <span class="tournament-card-countdown">${initialCountdown}</span>
      <div class="tournament-card-actions"></div>
    </div>
  `;
  return card;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
